import type { FC, ReactNode } from 'react'

import { ArrowUpRight, Repeat } from 'lucide-react'
import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'
import { SectionLabel } from '@/components/generals/section-label'

import { PROCESS } from '@/constants/agency'

interface ProcessSectionProps {
    label?: string
    intro?: string
}

/* On large screens each step sits one riser higher than the last, so the four steps read as a climb. */
const RISE = ['lg:mt-48', 'lg:mt-32', 'lg:mt-16', 'lg:mt-0']

const ProcessSection: FC<ProcessSectionProps> = ({
    label = 'Our process / 03',
    intro = "A clear, repeatable way of working that keeps strategy, creative and performance moving in the same direction."
}): ReactNode => {
    return (
        <section id='process' aria-labelledby='process-title' data-nav-theme='light' className='relative overflow-hidden bg-coral text-charcoal'>
            <div className='relative mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28'>
                <div className='grid gap-8 lg:grid-cols-12 lg:items-end lg:gap-10'>
                    <div data-reveal className='lg:col-span-7'>
                        <SectionLabel className='text-charcoal'>{label}</SectionLabel>
                        <h2 id='process-title' className='mt-6 font-heading type-section'>
                            <span className='md:block'>From first idea</span>{' '}
                            <span className='md:block'>
                                to <span className='bg-cream px-[0.08em] [box-decoration-break:clone] [-webkit-box-decoration-break:clone]'>real growth.</span>
                            </span>
                        </h2>
                    </div>
                    <p data-reveal className='max-w-md text-[17px] leading-relaxed lg:col-span-4 lg:col-start-9'>
                        {intro}
                    </p>
                </div>

                <div className='relative mt-14 md:mt-16 lg:mt-20'>
                    <ol className='lg:grid lg:grid-cols-4'>
                        {PROCESS.map((step, index) => {
                            const last = index === PROCESS.length - 1

                            return (
                                <li
                                    key={step.title}
                                    data-reveal
                                    className={cn(
                                        'relative border-l-2 border-charcoal pb-12 pl-8 last:pb-0 lg:border-l-0 lg:border-t-2 lg:pb-0 lg:pl-0 lg:pr-8 lg:pt-8',
                                        RISE[index]
                                    )}
                                >
                                    <span aria-hidden='true' className='absolute -left-[7px] top-4 size-3 bg-charcoal lg:-left-[5px] lg:-top-[7px]' />
                                    {index > 0 && (
                                        <span aria-hidden='true' className='absolute left-0 top-[-2px] hidden h-[calc(4rem+2px)] w-0.5 bg-charcoal lg:block' />
                                    )}
                                    {last && (
                                        <ArrowUpRight aria-hidden='true' className='absolute -right-1 -top-[13px] hidden size-6 lg:block' />
                                    )}

                                    <div className='flex items-baseline gap-4 lg:block'>
                                        <span className='text-outline-charcoal font-heading font-wide text-5xl font-black leading-none tracking-[-0.05em] lg:text-[clamp(3.5rem,5vw,5rem)]'>
                                            {pad(index + 1)}
                                        </span>
                                        <h3 className='font-heading font-wide text-xl font-black uppercase tracking-[-0.015em] lg:mt-6 lg:text-[1.35rem]'>
                                            {step.title}
                                        </h3>
                                    </div>
                                    <p className='mt-3 max-w-xs text-[15px] leading-relaxed'>
                                        {step.description}
                                    </p>
                                </li>
                            )
                        })}
                    </ol>

                    <p className='mt-12 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] lg:absolute lg:bottom-0 lg:right-0 lg:mt-0 lg:max-w-[22%]'>
                        <Repeat aria-hidden='true' className='size-4 shrink-0' />
                        Then we measure, learn and go again.
                    </p>
                </div>
            </div>
        </section>
    )
}

export default ProcessSection
